"use client";

// Privacy-first analytics loader. Single component, picks the provider
// from env at build time (NEXT_PUBLIC_* — must be set when `next build`
// runs). No provider configured = no script loads, no cookies, no
// network calls, zero overhead.
//
// Supported providers — pick one:
//
//   plausible  — set NEXT_PUBLIC_ANALYTICS_PROVIDER=plausible
//                set NEXT_PUBLIC_PLAUSIBLE_DOMAIN=smokoin.app
//                Optional: NEXT_PUBLIC_PLAUSIBLE_HOST=plausible.io
//                  (override only if self-hosting or proxying via your
//                   own subdomain to dodge ad-blockers)
//                Top-of-funnel only. ~1KB script, no cookies, no PII.
//
//   posthog    — set NEXT_PUBLIC_ANALYTICS_PROVIDER=posthog
//                set NEXT_PUBLIC_POSTHOG_KEY=<phc_...>
//                Optional: NEXT_PUBLIC_POSTHOG_HOST=https://us.i.posthog.com
//                Full funnel + session replay + feature flags. Free up
//                to 1M events/mo. Configured below to autocapture page
//                views but disable session recording by default — flip
//                via env if needed.
//
//   cloudflare — set NEXT_PUBLIC_ANALYTICS_PROVIDER=cloudflare
//                set NEXT_PUBLIC_CLOUDFLARE_BEACON_TOKEN=<token>
//                Free Cloudflare Web Analytics. Zero-cookie pageviews.
//                Best if your domain is on Cloudflare DNS already.
//
//   none / unset — no analytics. Default for dev.
//
// All of these are no-cookie / GDPR-friendly out of the box and don't
// trigger consent-banner requirements. If you ever swap to GA4 you'll
// need a banner separately.

import Script from "next/script";

const PROVIDER = process.env.NEXT_PUBLIC_ANALYTICS_PROVIDER;

export default function Analytics() {
  if (PROVIDER === "plausible") return <Plausible />;
  if (PROVIDER === "posthog")    return <PostHog />;
  if (PROVIDER === "cloudflare") return <Cloudflare />;
  return null;
}

// ─── Plausible ───────────────────────────────────────────────────────

function Plausible() {
  const domain = process.env.NEXT_PUBLIC_PLAUSIBLE_DOMAIN;
  const host = process.env.NEXT_PUBLIC_PLAUSIBLE_HOST ?? "plausible.io";
  if (!domain) return null;
  return (
    <Script
      src={`https://${host}/js/script.js`}
      data-domain={domain}
      // `defer` not `async` — Plausible's snippet relies on DOMContentLoaded
      // ordering for the initial pageview event.
      strategy="afterInteractive"
    />
  );
}

// ─── PostHog ─────────────────────────────────────────────────────────

function PostHog() {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";
  if (!key) return null;
  // Inline init script — keeps the loader lean and avoids a second
  // round-trip. Recordings disabled by default; flip via the dashboard
  // or by adding session_recording: { enabled: true } below.
  const init = `
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    posthog.init('${key}', {
      api_host: '${host}',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      // No PII collection: never capture form values, ignore inputs by
      // default. Session recording stays off — flip in dashboard if
      // ever needed.
      disable_session_recording: true,
      mask_all_text: false,
      mask_all_element_attributes: false,
      respect_dnt: true,
    });
  `;
  return (
    <Script id="posthog-init" strategy="afterInteractive">
      {init}
    </Script>
  );
}

// ─── Cloudflare Web Analytics ────────────────────────────────────────

function Cloudflare() {
  const beacon = process.env.NEXT_PUBLIC_CLOUDFLARE_BEACON_TOKEN;
  if (!beacon) return null;
  return (
    <Script
      src="https://static.cloudflareinsights.com/beacon.min.js"
      data-cf-beacon={JSON.stringify({ token: beacon })}
      strategy="afterInteractive"
    />
  );
}
